import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import { OverlayState } from '@/hooks/useOverlay'
import { useCreateProjectCategory } from '@/service-library/hooks/project-categories'
import { showErrorSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'
import { useCurrentAccountContext } from '@/components/accounts/CurrentAccountProvider'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { FormTextField, PixyDocsForm } from '@/components/forms'

type AddProjectCategoryProps = {
  overlay: OverlayState
  listQueryKey: QueryKey
}

type FormValues = {
  name: string
}

export default function AddProjectCategoryDialog({
  overlay,
  listQueryKey,
}: AddProjectCategoryProps) {
  const { rootOrganization } = useCurrentAccountContext()

  const { createProjectCategory } = useCreateProjectCategory({
    listQueryKey,
    onError: () => {
      showErrorSnackbar('Unable to create category. Please try again later.')
    },
  })

  const methods = useForm<FormValues>({
    defaultValues: { name: '' },
  })

  const {
    formState: { isDirty, isSubmitSuccessful, isValid },
    reset,
  } = methods

  function handleCreateProjectCategory(values: FormValues) {
    values.name = values.name.trim()
    createProjectCategory({
      ...values,
      id: generateUuid(),
      sort_order: 1000001,
      org_id: rootOrganization.id,
    })
    overlay.close()
  }

  // From useForm docs: 'It's recommended to reset inside useEffect after submission.'
  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({ name: '' })
    }
  }, [isSubmitSuccessful, reset])

  return (
    <Dialog title="New Category" {...overlay} maxWidth="sm">
      <PixyDocsForm methods={methods} onSubmit={handleCreateProjectCategory}>
        <DialogContent>
          <FormTextField
            name="name"
            autoFocus
            fullWidth
            label="Name"
            required
            useControllerProps={{
              rules: {
                validate: (value) => value.trim().length > 0,
              },
            }}
          />
        </DialogContent>
        <DialogFooter>
          <Button variant="text" onClick={overlay.close}>
            Cancel
          </Button>
          <Button type="submit" disabled={!isDirty || !isValid}>
            Save
          </Button>
        </DialogFooter>
      </PixyDocsForm>
    </Dialog>
  )
}
